import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Heart, ArrowLeft, Loader2, Gift, Check } from "lucide-react";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  
  // Login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  
  // Register state
  const [registerName, setRegisterName] = useState("");
  const [registerEmail, setRegisterEmail] = useState("");
  const [registerPassword, setRegisterPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [referralValid, setReferralValid] = useState(null);
  const [referralMessage, setReferralMessage] = useState("");
  const [referralExpired, setReferralExpired] = useState(false);

  // Check for referral code in URL
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref.toUpperCase());
      validateReferralCode(ref.toUpperCase());
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    if (!code || code.length < 3) {
      setReferralValid(null);
      setReferralMessage("");
      setReferralExpired(false);
      return;
    }
    
    try {
      const res = await axios.post(`${API}/referrals/validate`, { code: code.toUpperCase() });
      setReferralValid(res.data.valid);
      setReferralMessage(res.data.message);
      setReferralExpired(res.data.expired || false);
    } catch (error) {
      setReferralValid(false);
      setReferralMessage("Invalid referral code");
      setReferralExpired(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginEmail, loginPassword);
      toast.success("Welcome back, beautiful soul!");
      if (result.user.quiz_completed) {
        navigate("/dashboard");
      } else {
        navigate("/quiz");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to sign in. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerEmail, registerPassword, registerName, referralCode || null);
      if (referralValid) {
        toast.success("Welcome! You've received 2 weeks free premium as a referral bonus! 🎁");
      } else {
        toast.success("Welcome to your journey of self-discovery!");
      }
      navigate("/quiz");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to create account. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex flex-col" data-testid="auth-page">
      {/* Back Button */}
      <div className="p-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="text-muted-foreground hover:text-foreground"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
      </div>

      {/* Auth Card */}
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground">Authentic Self 365</h1>
            <p className="text-muted-foreground mt-2">Your sacred space awaits</p>
          </div>

          <Card className="glass-card rounded-3xl border-0">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-muted/50 rounded-2xl p-1 mx-auto max-w-[90%] mt-6">
                <TabsTrigger 
                  value="login" 
                  className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="login-tab"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  className="rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm"
                  data-testid="register-tab"
                >
                  Join Us
                </TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <CardHeader className="text-center pb-4">
                  <CardTitle className="font-heading text-xl">Welcome Back</CardTitle>
                  <CardDescription>Continue your journey of self-discovery</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleLogin} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-sm font-medium">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="rounded-xl h-12 bg-white/50 border-border focus:border-primary/50"
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password" className="text-sm font-medium">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="rounded-xl h-12 bg-white/50 border-border focus:border-primary/50"
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      className="w-full btn-primary h-12"
                      disabled={loading}
                      data-testid="login-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        "Sign In"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <CardHeader className="text-center pb-4">
                  <CardTitle className="font-heading text-xl">Begin Your Journey</CardTitle>
                  <CardDescription>Create your sacred space for self-discovery</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-sm font-medium">Your Name</Label>
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="How shall we call you?"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        className="rounded-xl h-12 bg-white/50 border-border focus:border-primary/50"
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-sm font-medium">Email</Label>
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="your@email.com"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        className="rounded-xl h-12 bg-white/50 border-border focus:border-primary/50"
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-sm font-medium">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="Create a secure password"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        className="rounded-xl h-12 bg-white/50 border-border focus:border-primary/50"
                        required
                        minLength={6}
                        data-testid="register-password-input"
                      />
                    </div>
                    
                    {/* Referral Code Field */}
                    <div className="space-y-2">
                      <Label htmlFor="referral-code" className="flex items-center gap-2 text-foreground/80">
                        <Gift className="w-4 h-4 text-amber-500" />
                        Referral Code (optional)
                      </Label>
                      <div className="relative">
                        <Input
                          id="referral-code"
                          type="text"
                          placeholder="e.g. JULIE-AS85F4"
                          value={referralCode}
                          onChange={(e) => {
                            const code = e.target.value.toUpperCase();
                            setReferralCode(code);
                            validateReferralCode(code);
                          }}
                          className={`rounded-xl h-12 bg-white/50 border-border focus:border-primary/50 uppercase ${
                            referralValid === true ? 'border-green-500 bg-green-50/50' : 
                            referralExpired ? 'border-amber-400 bg-amber-50/50' :
                            referralValid === false ? 'border-red-300 bg-red-50/50' : ''
                          }`}
                          data-testid="referral-code-input"
                        />
                        {referralValid === true && (
                          <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                        )}
                      </div>
                      {referralMessage && (
                        <p className={`text-xs ${
                          referralValid ? 'text-green-600' : 
                          referralExpired ? 'text-amber-600' : 
                          'text-red-500'
                        }`}>
                          {referralMessage}
                        </p>
                      )}
                    </div>
                    
                    <Button
                      type="submit"
                      className="w-full btn-primary h-12"
                      disabled={loading}
                      data-testid="register-submit-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        "Create My Space"
                      )}
                    </Button>
                  </form>
                </CardContent>
              </TabsContent>
            </Tabs>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            By continuing, you agree to nurture yourself with kindness
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
